/**
 * Multi-tenant theming system
 * Each theme defines colors (HSL values), favicon, and branding
 */

export interface Theme {
  id: string;
  name: string;           // Display name for branding
  brandText: string;      // Full brand text (e.g., "Wehkamp Demo")
  favicon: string;        // Base64 SVG data URL
  logo?: string;          // Logo URL (optional)
  headerBackground?: string; // Optional different header background (HSL)
  heroTitle?: string;        // Custom hero title (overrides generic "Welkom bij de X Voice Demo")
  heroSubtitle?: string;     // Custom hero subtitle
  colors: {
    // Core colors (HSL values without hsl() wrapper)
    primary: string;
    primaryForeground: string;
    secondary: string;
    secondaryForeground: string;
    accent: string;
    accentForeground: string;
    background: string;
    foreground: string;
    muted: string;
    mutedForeground: string;
    card: string;
    cardForeground: string;
    border: string;
    input: string;
    ring: string;
    destructive: string;
    destructiveForeground: string;
    success: string;
    successForeground: string;
    warning: string;
    warningForeground: string;
  };
  // Glow color for effects (rgba format)
  glowColor: string;
  // Semi-transparent surface for glass effects
  surfaceAlpha: string;
  // Gradient endpoints for hero/splash sections (HSL values)
  gradientStart: string;
  gradientEnd: string;
}

// Wehkamp coral theme (current default)
const wehkampTheme: Theme = {
  id: 'wehkamp',
  name: 'Wehkamp',
  brandText: 'Wehkamp Demo',
  // Coral "W" on rounded rectangle
  favicon: "data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHZpZXdCb3g9JzAgMCAxMDAgMTAwJz48cmVjdCBmaWxsPScjZmY1NTU1JyByeD0nMTInIHdpZHRoPScxMDAnIGhlaWdodD0nMTAwJy8+PHRleHQgeT0nLjllbScgeD0nNTAlJyB0ZXh0LWFuY2hvcj0nbWlkZGxlJyBmb250LXNpemU9JzcwJyBmb250LXdlaWdodD0nYm9sZCcgZmlsbD0nd2hpdGUnPlc8L3RleHQ+PC9zdmc+",
  colors: {
    primary: '0 100% 67%',              // #f55 coral
    primaryForeground: '0 0% 100%',
    secondary: '210 17% 25%',           // Navy/Slate
    secondaryForeground: '0 0% 98%',
    accent: '185 71% 14%',              // #0a373b seagreen
    accentForeground: '0 0% 98%',
    background: '210 20% 8%',           // Dark slate
    foreground: '0 0% 98%',
    muted: '210 15% 15%',
    mutedForeground: '210 10% 60%',
    card: '210 20% 10%',
    cardForeground: '0 0% 98%',
    border: '210 15% 20%',
    input: '210 15% 15%',
    ring: '0 100% 67%',                 // Coral ring
    destructive: '0 84% 60%',
    destructiveForeground: '0 0% 98%',
    success: '142 76% 45%',
    successForeground: '0 0% 98%',
    warning: '38 92% 50%',
    warningForeground: '210 20% 10%',
  },
  glowColor: 'rgba(255, 85, 85, 0.3)',
  surfaceAlpha: 'rgba(24, 32, 43, 0.6)',
  gradientStart: '0 100% 67%',
  gradientEnd: '20 90% 55%',
};

// NextNovate yellow/grey theme
const nextnovateTheme: Theme = {
  id: 'nextnovate',
  name: 'NextNovate',
  brandText: 'NextNovate Demo',
  // Yellow circle with NextNovate beeldmerk
  favicon: "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMDAgMTAwIj4KICA8Y2lyY2xlIGN4PSI1MCIgY3k9IjUwIiByPSI1MCIgZmlsbD0iI0ZCQjMwMCIvPgogIDxnIHRyYW5zZm9ybT0idHJhbnNsYXRlKDIwLCA3KSBzY2FsZSgwLjk4KSI+CiAgICA8cGF0aCBmaWxsPSIjZmZmIiBkPSJNMjguNSwyOS4yYzAsNS44LDQuNywxMC42LDEwLjYsMTAuNmM1LjgsMCwxMC42LTQuNywxMC42LTEwLjZjMC01LjgtNC43LTEwLjYtMTAuNi0xMC42IEMzMy4zLDE4LjYsMjguNSwyMy4zLDI4LjUsMjkuMnogTTQ0LjEsMjkuMmMwLDIuOC0yLjIsNS01LDVjLTIuOCwwLTUtMi4yLTUtNWMwLTIuOCwyLjItNSw1LTVDNDEuOSwyNC4yLDQ0LjEsMjYuNCw0NC4xLDI5LjJ6Ii8+CiAgICA8cGF0aCBmaWxsPSIjZmZmIiBkPSJNNDkuMSw0My42Yy0wLjUtMC4xLTEzLjEtMS4zLTE5LjYsNC42Yy0yLjUsMi4zLTMuOCw1LjQtMy44LDguOXYwLjdjLTcuOC00LjYtMTMuMS0xMy4xLTEzLjEtMjIuOSBjMC0xNC42LDExLjktMjYuNSwyNi41LTI2LjVWMi44QzIxLjQsMi44LDcsMTcuMiw3LDM0LjlzMTQuNCwzMi4xLDMyLjEsMzIuMXYtNS42Yy0yLjcsMC01LjQtMC40LTcuOS0xLjJ2LTMuMmMwLTIsMC43LTMuNiwyLTQuOCBjMy0yLjcsOC45LTMuMywxMi44LTMuM2wwLDE4LjhjMCwyLTAuNywzLjYtMiw0LjhjLTMsMi43LTguOSwzLjMtMTIuNywzLjN2LTUuM2gtNS42VjgxbDIuNSwwLjNjMC4yLDAsMS41LDAuMSwzLjQsMC4xIGM0LjMsMCwxMS43LTAuNywxNi4yLTQuN2MyLjUtMi4zLDMuOS01LjQsMy45LTguOWwwLTIzLjlMNDkuMSw0My42eiIvPgogIDwvZz4KPC9zdmc+Cg==",
  logo: "https://www.nextnovate.com/wp-content/uploads/logo-default-no-slogan.svg",
  colors: {
    primary: '45 100% 49%',             // #FBB300 yellow
    primaryForeground: '0 0% 100%',     // White text on yellow
    secondary: '197 79% 64%',           // #5ABFEC blue
    secondaryForeground: '0 0% 100%',
    accent: '117 59% 70%',              // #89E185 green
    accentForeground: '0 0% 100%',
    background: '220 20% 10%',          // Dark background
    foreground: '0 0% 98%',             // White text
    muted: '220 15% 15%',
    mutedForeground: '220 10% 60%',
    card: '220 20% 12%',
    cardForeground: '0 0% 98%',
    border: '220 15% 20%',
    input: '220 15% 15%',
    ring: '45 100% 49%',                // Yellow ring
    destructive: '0 84% 60%',
    destructiveForeground: '0 0% 98%',
    success: '117 59% 50%',
    successForeground: '0 0% 98%',
    warning: '38 100% 47%',             // #F29100 orange
    warningForeground: '0 0% 100%',
  },
  // Light header so dark logo is visible
  headerBackground: '45 100% 49%',      // Yellow header!
  glowColor: 'rgba(251, 179, 0, 0.3)',
  surfaceAlpha: 'rgba(26, 32, 44, 0.6)',
  gradientStart: '45 100% 49%',
  gradientEnd: '38 100% 47%',
};

// Brevox electric blue theme
const brevoxTheme: Theme = {
  id: 'brevox',
  name: 'Brevox',
  brandText: 'Brevox — The Call Center That Never Sleeps',
  heroTitle: 'The Call Center That Never Sleeps',
  heroSubtitle: 'AI voice agents that sell, solve & scale — 24/7, in any language.',
  // Electric blue "K" on rounded rectangle
  favicon: "data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHZpZXdCb3g9JzAgMCAxMDAgMTAwJz48cmVjdCBmaWxsPScjM0I4MkY2JyByeD0nMTYnIHdpZHRoPScxMDAnIGhlaWdodD0nMTAwJy8+PHRleHQgeT0nLjllbScgeD0nNTAlJyB0ZXh0LWFuY2hvcj0nbWlkZGxlJyBmb250LXNpemU9JzY4JyBmb250LXdlaWdodD0nYm9sZCcgZmlsbD0nd2hpdGUnPkI8L3RleHQ+PC9zdmc+",
  colors: {
    primary: '217 91% 60%',              // #3B82F6 electric blue
    primaryForeground: '0 0% 100%',
    secondary: '215 50% 23%',            // Deep navy slate
    secondaryForeground: '0 0% 98%',
    accent: '190 85% 48%',              // Bright cyan
    accentForeground: '0 0% 100%',
    background: '222 30% 7%',            // Very dark navy
    foreground: '0 0% 98%',
    muted: '220 20% 14%',
    mutedForeground: '220 12% 55%',
    card: '222 28% 10%',
    cardForeground: '0 0% 98%',
    border: '220 18% 18%',
    input: '220 18% 14%',
    ring: '217 91% 60%',                 // Blue ring
    destructive: '0 84% 60%',
    destructiveForeground: '0 0% 98%',
    success: '142 76% 45%',
    successForeground: '0 0% 98%',
    warning: '38 92% 50%',
    warningForeground: '222 30% 10%',
  },
  glowColor: 'rgba(59, 130, 246, 0.3)',
  surfaceAlpha: 'rgba(18, 24, 38, 0.6)',
  gradientStart: '217 91% 60%',
  gradientEnd: '260 70% 55%',
};

// All available themes
export const themes: Record<string, Theme> = {
  wehkamp: wehkampTheme,
  nextnovate: nextnovateTheme,
  brevox: brevoxTheme,
};

// Default theme for users without assignment
export const DEFAULT_THEME = 'nextnovate';

// Default theme for admins (NextNovate)
export const ADMIN_DEFAULT_THEME = 'nextnovate';

// Get theme by ID with fallback
export function getTheme(themeId: string | undefined): Theme {
  return themes[themeId || DEFAULT_THEME] || themes[DEFAULT_THEME];
}

// Get list of all theme IDs
export function getThemeIds(): string[] {
  return Object.keys(themes);
}
